import { Component, OnInit, Inject, Injector, OnDestroy } from "@angular/core";
import { DateAdapter, MatDialogRef, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_DIALOG_DATA } from "@angular/material";

import { LoggingService } from "../../../service/logging.service";
import { Logger } from "../../../model/logger.model";
import { AdvancedForm, AdvancedFormBuilder } from "../../../model/advanced-form.model";
import { HolidayEditDialogPayload } from "../../../interface/type/holiday-edit-dialog-payload.type";
import { HolidayEditForm } from "../../../model/form/holiday-edit.form";
import { Holiday } from "../../../persistence/dto/holiday.model";
import { HolidayType, toHolidayTypeString } from "../../../persistence/dto/holiday-type.enum";
import { Enum } from "../../../support/enum.support";
import { HolidayEditDialogResult } from "../../../interface/type/holiday-edit-dialog-result.type";
import { HolidayService } from "../../../service/holiday.service";
import { Unsubscriber } from "../../../support/unsubscriber.support";
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from "@angular/material-moment-adapter";

@Component({
	selector: "holiday-edit-dialog-",
	templateUrl: "./holiday-edit-dialog.component.html",
	styleUrls: ["../../style/main.scss", "./holiday-edit-dialog.component.scss"],
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: "es-AR" },
		{ provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
		{ provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS }
	]
})
export class HolidayEditDialogComponent implements OnInit, OnDestroy {

	/**
	 * @description
	 *	Día no-laborable a crear, editar, eliminar.
	 */
	private holiday: Holiday = Holiday.empty();

	/**
	 * @description
	 *	Logger principal del componente.
	 */
	private logger: Logger = this.loggingService.getLogger("holiday-edit-dialog.component");

	/**
	 * @description
	 *	Wrapper de subscripciones.
	 */
	private unsubscriber: Unsubscriber = new Unsubscriber();

	/**
	 * @description
	 *	Formulario principal de la vista.
	 */
	public advancedForm: AdvancedForm;

	/**
	 * @description
	 *	Todos los tipos de día no-laborables disponibles.
	 */
	public holidayTypes: HolidayType[] = Enum.valuesOf(HolidayType, "string");

	/**
	 * @description
	 *	Fecha más antigua y más moderna disponible en el calendario.
	 */
	public minDate: Date = new Date("1970-01-01T00:00:00.000-03:00");
	public maxDate: Date = undefined;

	constructor(
		private holidayService: HolidayService,
		private injector: Injector,
		private loggingService: LoggingService,
		private matDialogRef: MatDialogRef<HolidayEditDialogComponent, HolidayEditDialogResult>,
		@Inject(MAT_DIALOG_DATA) public payload: HolidayEditDialogPayload) {}

	public ngOnInit(): void {
		this.logger.debug("Payload:", this.payload);
		this.advancedForm = new AdvancedFormBuilder()
			.injector(this.injector)
			.logger(this.loggingService.getLogger("holiday-edit-dialog.form"))
			.specification(HolidayEditForm)
			.build()
			.patch({ date: this.payload.date });
		this.unsubscriber
			.addSubscription(this.advancedForm.isChanging()
			.subscribe({
				next: values => {
					const newDate: Date = new Date(values.date);
					if (this.isChangingDate(newDate)) {
						this.holidayService
							.findHolidaysByDate(newDate)
							.subscribe({
								next: holidays => {
									if (0 < holidays.length) {
										this.holiday.date = new Date(holidays[0].date);
										this.holiday.description = holidays[0].description;
										this.holiday.id = holidays[0].id;
										this.holiday.type = holidays[0].type;
										this.advancedForm.patch({
											description: this.holiday.description,
											date: this.holiday.date,
											type: this.holiday.type
										});
									}
									else {
										this.holiday.date = newDate;
										this.holiday.description = values.description;
										this.holiday.id = undefined;
										this.holiday.type = values.type;
									}
								},
								error: (error: Error) => this.logger.error(error)
							});
					}
					else {
						this.holiday.description = values.description;
						this.holiday.type = values.type;
					}
				},
				error: (error: Error) => this.logger.error(error)
			}));
	}

	public ngOnDestroy(): void {
		this.unsubscriber.releaseSubscriptions();
	}

	public isChangingDate(newDate: Date): boolean {
		if (this.holiday.date === null || this.holiday.date === undefined) {
			if (newDate === null || newDate === undefined) {
				return false;
			}
			else {
				return true;
			}
		}
		else {
			if (newDate === null || newDate === undefined) {
				this.logger.fatal("The new date is undefined, and it can't be possible.");
				return true;
			}
			else {
				const oldDateString = this.holiday.date.toLocaleDateString();
				const newDateString = newDate.toLocaleDateString();
				return oldDateString !== newDateString;
			}
		}
	}

	public isCreating(): boolean {
		return (this.holiday.id === null || this.holiday.id === undefined)
			&& this.holiday.type === HolidayType.Holiday;
	}

	public isDeleting(): boolean {
		return this.holiday.id !== null
			&& this.holiday.id !== undefined
			&& this.holiday.type !== HolidayType.Holiday;
	}

	public isUpdating(): boolean {
		return this.holiday.id !== null
			&& this.holiday.id !== undefined
			&& this.holiday.type === HolidayType.Holiday;
	}

	public onApply(): void {
		if (this.isCreating()) {
			this.logger.debug("Creating a new holiday...");
			this.holidayService
				.createHoliday(this.holiday)
				.subscribe({
					next: holiday => this.close(true, holiday),
					error: (error: Error) => {
						this.logger.error(error);
						this.onCancel();
					}
				});
		}
		else if (this.isDeleting()) {
			this.logger.debug("Deleting holiday...");
			this.holidayService
				.deleteHoliday(this.holiday.id)
				.subscribe({
					next: _ => this.close(true, undefined),
					error: (error: Error) => {
						this.logger.error(error);
						this.onCancel();
					}
				});
		}
		else if (this.isUpdating()) {
			this.logger.debug("Updating holiday...");
			this.holidayService
				.updateHoliday(this.holiday)
				.subscribe({
					next: _ => this.close(true, this.holiday),
					error: (error: Error) => {
						this.logger.error(error);
						this.onCancel();
					}
				});
		}
		else {
			this.logger.error("Cannot create a new holiday with an empty type:", this.holiday);
			this.onCancel();
		}
	}

	public onCancel(): void {
		this.close(false, undefined);
	}

	public toHolidayTypeString(holidayType: HolidayType): string {
		return toHolidayTypeString(holidayType);
	}

	private close(ok: boolean, holiday: Holiday): void {
		this.matDialogRef.close({ ok, holiday });
	}
}
